/**
 * Browser translation (Chrome/Google Translate) swaps text nodes for `<font>`
 * wrappers, so React's later removeChild/insertBefore on those nodes throws
 * NotFoundError and crashes the app. Make both calls tolerate a re-parented
 * node instead. https://github.com/facebook/react/issues/11538
 */
import { logger } from "@/lib/logger";

let installed = false;
let warned = false;

function warnOnce(op: "removeChild" | "insertBefore", node: Node): void {
  if (warned) return;
  warned = true;
  logger.warn("DOM mutated outside React (likely browser translation)", {
    op,
    nodeName: node.nodeName,
    htmlLang: document.documentElement.lang,
    translated:
      document.documentElement.classList.contains("translated-ltr") ||
      document.documentElement.classList.contains("translated-rtl"),
  });
}

export function installTranslateGuard(): void {
  if (installed || typeof Node !== "function" || !Node.prototype) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      warnOnce("removeChild", child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      warnOnce("insertBefore", referenceNode);
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

/** Test-only: reset the once-per-page-load warning latch. */
export function __resetTranslateGuardWarningForTests(): void {
  warned = false;
}
